import React, { Fragment, useState } from 'react';
import { Form, Formik } from 'formik';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useSelector } from 'react-redux';
import { getEnabledMatrix } from '../../../../../common/components/ProjectRoleUsersField/UserSettingsRightsMatrix.ts';
import Separator from '../../../../../common/components/Separator';
import {
  ProjectRoleUsersField,
  userPillId,
} from '../../../../../common/components/ProjectRoleUsersField/ProjectRoleUsersField';
import FormBottomActionsContainer from '../../common/components/FormBottomActionsContainer';
import CancelButtonLink from '../../common/components/CancelButtonLink';
import PublishButton from '../../../../../common/components/PublishButton';
import D3Alert from '../../../../../common/components/Alert/D3Alert';
import './UserEditSettings.scss';
import { getProjectSource } from '../../../projectSlice';

const getDefaultInitialValues = () =>
  Constants.USER_ROLES_LIST.reduce(
    (init, role) => ({
      ...(init || {}),
      [role]: [],
    }),
    {}
  );

export const getInitialState = (existingUsers = []) =>
  existingUsers
    .map((user) => ({
      ...user,
      role: user.roles[0].name,
      pendingApproval: user.roles[0].pendingApproval,
      wasPending: user.roles[0].pendingApproval,
    }))
    .map((user) => ({ ...user, pillId: userPillId(user) }))
    .reduce(
      (vals, user) => ({
        ...vals,
        [user.role]: [...(vals[user.role] || []), user],
      }),
      getDefaultInitialValues()
    );

const UserEditSettings = ({
  initialValues = {},
  fieldConfigs = [],
  onSubmit,
  submitButtonText = 'Save',
  onCancel,
  children,
  isSaving,
  hasError,
  isDirty,
  withRoleChange,
}) => {
  const [usersToDelete, setUsersToDelete] = useState([]);
  const [usersToAdd, setUsersToAdd] = useState([]);
  const [usersToChangeRole, setUsersToChangeRole] = useState([]);
  const [usersToApprove, setUsersToApprove] = useState([]);
  const projectSource = useSelector(getProjectSource);

  const addUsersToDelete = (user) => {
    const isUserAddedJustLocally = usersToAdd.find(
      ({ email }) => email === user.email
    );
    const isUserChangedJustLocally = usersToChangeRole.find(
      ({ email }) => email === user.email
    );
    if (isUserAddedJustLocally) {
      setUsersToAdd(usersToAdd.filter(({ email }) => email !== user.email));
    } else if (isUserChangedJustLocally) {
      setUsersToChangeRole(
        usersToAdd.filter(({ email }) => email !== user.email)
      );
      setUsersToDelete([...usersToDelete, user]);
    } else {
      setUsersToDelete([...usersToDelete, user]);
    }
  };

  const addUsersToAdd = (user) => {
    const isUserDeletedJustLocally = usersToDelete.find(
      ({ email }) => email === user.email
    );
    if (isUserDeletedJustLocally) {
      const isUserDeletedJustLocallyWithSameRole = usersToDelete.find(
        ({ email, role }) => email === user.email && role === user.role
      );
      setUsersToDelete(
        usersToDelete.filter(({ email }) => email !== user.email)
      );
      if (!isUserDeletedJustLocallyWithSameRole) {
        setUsersToChangeRole([...usersToChangeRole, user]);
      }
    } else {
      setUsersToAdd([...usersToAdd, user]);
    }
  };

  const addUsersToChangeRole = (user) => {
    const isUserAlreadyUpdatedLocally = usersToChangeRole.find(
      ({ id }) => id === user.id
    );
    if (isUserAlreadyUpdatedLocally) {
      setUsersToChangeRole(
        usersToChangeRole.map((u) => (u.id === user.id ? user : u))
      );
    } else {
      setUsersToChangeRole([...usersToChangeRole, user]);
    }
  };

  const addUsersToApprove = (user) => {
    setUsersToApprove([...usersToApprove, user]);
  };

  const submit = (values) => {
    const formattedValues = Constants.USER_ROLES_LIST.map(
      (role) => values[role]
    ).flat();
    return onSubmit(formattedValues, {
      usersToAdd,
      usersToDelete,
      usersToChangeRole,
      usersToApprove,
    });
  };

  const cancel = (resetForm) => {
    setUsersToDelete([]);
    setUsersToAdd([]);
    setUsersToChangeRole([]);
    setUsersToApprove([]);
    onCancel(resetForm);
  };

  const flattenValuesWrapper = (fn) => (values) => {
    const formattedValues = Constants.USER_ROLES_LIST.map(
      (role) => values[role]
    ).flat();
    return fn(formattedValues);
  };

  const checkIsValid = (values) => {
    const requiredConfigs = fieldConfigs.filter((fc) => fc.required);
    const isValid = requiredConfigs.every(({ role, allowMany }) =>
      allowMany
        ? (values[role] || []).length > 0
        : (values[role] || []).length === 1
    );
    return isValid;
  };
  const roles = fieldConfigs
    .map(({ role }) => role)
    .filter(
      (role) =>
        (getEnabledMatrix(projectSource, role, !!withRoleChange) || {})
          .isChangeEnabled
    );

  return (
    <div className="project-user-form" data-testid="projectUserForm">
      <div>{children}</div>
      <Formik {...{ initialValues: initialValues || {}, onSubmit: submit }}>
        {({ dirty, values, resetForm }) => {
          return (
            <Form>
              {fieldConfigs.map((fieldConfig) => (
                <Fragment key={fieldConfig.role}>
                  <ProjectRoleUsersField
                    {...fieldConfig}
                    addUsersToAdd={addUsersToAdd}
                    addUsersToDelete={addUsersToDelete}
                    addUsersToApprove={addUsersToApprove}
                    addUsersToChangeRole={addUsersToChangeRole}
                    withRoleChange={withRoleChange}
                    roles={roles}
                    isCreateEnabled
                  />
                  <Separator />
                </Fragment>
              ))}
              <FormBottomActionsContainer>
                {onCancel && (
                  <CancelButtonLink onClick={() => cancel(resetForm)} />
                )}
                {onSubmit && (
                  <PublishButton
                    isValid={checkIsValid(values)}
                    isSaving={isSaving}
                    dirty={isDirty === undefined ? dirty : isDirty}
                    buttonSize="sm"
                    standbyText={
                      typeof submitButtonText === 'string'
                        ? submitButtonText
                        : flattenValuesWrapper(submitButtonText)(values)
                    }
                    buttonPosition="project-user-details"
                  />
                )}
              </FormBottomActionsContainer>
              {hasError && (
                <div>
                  <br />
                  <D3Alert />
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
export default UserEditSettings;
